<template>
    <div class="summary">
        <div
            v-if="isConfirmationLoadingError"
            class="network-error-confirm-charge-stage"
            data-qa="network-error-confirm-charge-stage"
        >
            <DsInlineAlert
                leading-icon
                type="warning"
                class="network-error"
                data-qa="network-error"
            >
                <template #default>
                    <p class="network-error-paragraph" data-qa="network-error-paragraph">
                        {{ $t('myaccount.quick.add.change.summary.confirm.error') }}

                        <a
                            class="contact-support-link"
                            data-qa="contact-support-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            :href="contactSupportLink"
                        >
                            {{ $t('myaccount.quick.add.change.summary.confirm.error.contact.support') }}
                        </a>
                    </p>
                </template>
            </DsInlineAlert>
        </div>

        <DsLink
            class="go-to-add-contacts-button"
            leading-icon="arrow-left"
            @click="goToSelectionStage"
        >
            {{ $t('myaccount.quick.add.change.summary.add.contacts.button') }}
        </DsLink>

        <template v-if="showLoading">
            <ds-placeholder
                class="title-placeholder"
                data-qa="title-placeholder"
                :rows="titlePlaceholder"
            />
            <ds-placeholder
                class="summary-placeholder"
                :rows="paragraphPlaceholder"
            />
        </template>

        <div v-else>
            <h4
                class="summary-title"
                data-qa="summary-title"
            >
                {{ $t('myaccount.quick.add.change.summary.title') }}
            </h4>
            <div class="summary-text" data-qa="summary-text">
                <p>
                    <span>
                        {{ summary }}
                    </span>
                    <span class="summary-charge">
                        {{ summaryCharge }}
                    </span>
                    <span>
                        {{ totalCharge }}
                    </span>
                </p>
                <p>
                    {{ futureStateSentence }}
                </p>
                <p>
                    {{ $t('myaccount.quick.add.change.summary.automatic.bill.notice') }}
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';

const ERROR = 'error';
const CONTACT_SUPPORT_LINK = 'https://help.infusionsoft.com/help/chat-status';
const SELECTION_STAGE = 0;

export default {
    props: {
        typeOfAddon: String,
        chargeAmount: String,
        changeEstimate: Object,
        currentTier: Number,
        selectedTier: Number,
        contactDetails: Object,
        subscriptionDetails: Object,
        confirmationLoadingStatus: String,
        selectedWizardStage: Number,
    },

    data() {
        return {
            titlePlaceholder: [
                { height: '2.5rem', boxes: [1] },
            ],
            paragraphPlaceholder: [
                { height: '1.25rem', boxes: [1] },
                { height: '1.25rem', boxes: [1] },
                { height: '1.25rem', boxes: [1] },
            ],
            contactSupportLink: CONTACT_SUPPORT_LINK,
        };
    },

    computed: {
        plan() {
            return this.subscriptionDetails?.estimate?.estimateLineItems?.find(({ type }) => type === 'PLAN') || {};
        },

        summary() {
            return this.$tc('myaccount.quick.add.change.summary.change', this.newAdditionalQuantityText, { quantity: this.newAdditionalQuantityText, addon: this.typeOfAddonFormatted });
        },

        summaryCharge() {
            return this.$tc('myaccount.quick.add.change.summary.charge', this.chargeAmount, { amount: this.getPrice(this.chargeAmount) });
        },

        totalCharge() {
            return this.$tc('myaccount.quick.add.change.summary.total.charge', this.subscriptionDetails?.nextBillingAt, {
                date: this.dateFormatDate(this.subscriptionDetails?.nextBillingAt),
                period: this.periodUnitLower,
                formattedPeriod: this.formattedPeriod,
                total: this.getPrice(this.newTotal),
            });
        },

        periodUnitLower() {
            return this.subscriptionDetails?.billingFrequency.toLowerCase();
        },

        formattedPeriod() {
            if (this.subscriptionDetails?.billingFrequency === 'YEAR') {
                return this.$t('myaccount.quick.add.yearly');
            }

            return this.$t('myaccount.quick.add.monthly');
        },

        typeOfAddonFormatted() {
            if (this.typeOfAddon === 'CONTACT') {
                return 'contacts';
            }

            return '';
        },

        addonsByType() {
            const addonsByType = {
                CONTACT: 0,
                USER: 0,
            };

            this.subscriptionDetails?.lineItems.forEach((addon) => {
                if (addon.type === 'CONTACT') {
                    addonsByType.CONTACT += Number(addon.quantity);
                } else if (addon.type === 'USER') {
                    addonsByType.USER += Number(addon.quantity);
                }
            });

            addonsByType.CONTACT += this.newAdditionalContactQuantityNumber;

            return addonsByType;
        },

        futureStateSentence() {
            let sentence = '';

            if (this.addonsByType.CONTACT !== 0 && this.addonsByType.USER !== 0) {
                sentence = this.$tc('myaccount.quick.add.change.summary.overview.two.items', this.addonsByType.CONTACT, {
                    name: this.plan.name,
                    numberOfContacts: this.numberWithCommas(this.addonsByType.CONTACT),
                    numberOfUsers: this.numberWithCommas(this.addonsByType.USER),
                });
            } else if (this.addonsByType.CONTACT !== 0) {
                sentence = this.$tc('myaccount.quick.add.change.summary.overview.contacts', this.addonsByType.CONTACT, {
                    name: this.plan.name,
                    numberOfContacts: this.numberWithCommas(this.addonsByType.CONTACT),
                });
            } else if (this.addonsByType.USER !== 0) {
                sentence = this.$tc('myaccount.quick.add.change.summary.overview.users', this.addonsByType.USER, {
                    name: this.plan.name,
                    numberOfUsers: this.numberWithCommas(this.addonsByType.USER),
                });
            }

            return sentence;
        },

        newAdditionalContactQuantityNumber() {
            return this.contactDetails?.contactPricingTiers.at(this.selectedTier)?.maxUnits - this.contactDetails?.contactPricingTiers.at(this.currentTier)?.maxUnits;
        },

        newTotal() {
            return this.changeEstimate?.nextTermEstimate?.total;
        },

        showLoading() {
            return Object.keys(this.changeEstimate).length === 0;
        },

        isConfirmationLoadingError() {
            return this.confirmationLoadingStatus === ERROR;
        },
    },

    methods: {
        dateFormatDate(date, format = 'll') {
            const formattedDate = moment.utc(date).isValid() ? date : new Date(date);

            return moment(formattedDate).format(format);
        },

        getPrice(price) {
            return `${this.subscriptionDetails?.currencySymbol}${this.numberWithCommas(this.numberWithFixedDecimal(price))}`;
        },

        newAdditionalQuantityText() {
            const newQuantity = this.contactDetails?.contactPricingTiers.at(this.selectedTier)?.maxUnits - this.contactDetails?.contactPricingTiers.at(this.currentTier)?.maxUnits;

            return this.numberWithCommas(newQuantity);
        },

        numberWithCommas(x) {
            if (!x) {
                return 0;
            }
            const parts = x.toString().split('.');

            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

            return parts.join('.');
        },

        numberWithFixedDecimal(x) {
            if (!x) {
                return '0';
            }

            return Number.parseFloat(x).toFixed(2);
        },

        goToSelectionStage() {
            this.$emit('set-selected-wizard-stage', SELECTION_STAGE);
        },
    },
};
</script>

<style lang="scss" scoped>
.go-to-add-contacts-button {
    padding: $spacing-150;
    margin-bottom: $spacing-200;
}

.title-placeholder {
    max-width: 15rem;
    margin-bottom: $spacing-100;
}

.summary-placeholder {
    max-width: 500px;
}

.summary-title {
    font-size: $font-size-xl;
    font-weight: $font-weight-bold;
    line-height: $line-height-xl;
    padding-bottom: $spacing-100;
}

.summary-text {
    font-weight: $font-weight-regular;
    line-height: $line-height-md;
    max-width: 500px;
    align-items: flex-start;
}

.summary-charge {
    font-weight: $font-weight-bold;
}

.network-error-confirm-charge-stage {
    padding-bottom: $spacing-200;
}

.network-error {
    display: flex;
    flex: auto;
    max-width: 500px;
}

.network-error-paragraph {
    font-size: $font-size-sm;
    margin: 0;
}
</style>
